import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllBills,
  getAllParties,
} from "../../redux/billSlice";
import { getAllPayments } from "../../redux/paymentSlice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import SearchableSelect from "../common/SearchableSelect";
import { Helmet } from "react-helmet-async";
import qrImage from "../../asset/qr.jpeg";

const BillReport = () => {
  const dispatch = useDispatch();
  const { bills, parties, loading } = useSelector((state) => state.bill);
  const { payments } = useSelector((state) => state.payment || { payments: [] });

  // Date range states
  const [dateRange, setDateRange] = useState("thisMonth"); // thisMonth, lastMonth, custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedParty, setSelectedParty] = useState("");
  const [filteredBills, setFilteredBills] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [partyDueSummary, setPartyDueSummary] = useState([]);

  const formatDate = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatMoney = (val) => `Rs.${(parseFloat(val) || 0).toFixed(2)}`;

  // Get date ranges: 1st to last day of month (full calendar month, no timezone issues)
  const getDateRanges = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const thisMonthStart = new Date(y, m, 1, 0, 0, 0, 0);
    const thisMonthEnd = new Date(y, m + 1, 0, 23, 59, 59, 999);
    const lastMonthStart = new Date(y, m - 1, 1, 0, 0, 0, 0);
    const lastMonthEnd = new Date(y, m, 0, 23, 59, 59, 999);
    return {
      thisMonth: { start: thisMonthStart, end: thisMonthEnd },
      lastMonth: { start: lastMonthStart, end: lastMonthEnd },
    };
  };

  // YYYY-MM-DD for date-only comparison (full calendar day, no timezone)
  const toDateKey = (d) => {
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return null;
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, "0");
    const day = String(x.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Load parties and bills
  useEffect(() => {
    dispatch(getAllParties());
    dispatch(getAllBills());
    dispatch(getAllPayments());
  }, [dispatch]);

  // Filter bills & payments based on date range and party
  useEffect(() => {
    const ranges = getDateRanges();
    const start =
      dateRange === "thisMonth"
        ? ranges.thisMonth.start
        : dateRange === "lastMonth"
        ? ranges.lastMonth.start
        : dateRange === "custom" && startDate
        ? new Date(startDate)
        : null;
    let end =
      dateRange === "thisMonth"
        ? ranges.thisMonth.end
        : dateRange === "lastMonth"
        ? ranges.lastMonth.end
        : dateRange === "custom" && endDate
        ? new Date(endDate)
        : null;
    // Custom end date: set to end of day so the whole last day is included
    if (end && dateRange === "custom") {
      end.setHours(23, 59, 59, 999);
    }

    // Compare by calendar date only so full month (1–31) is included regardless of time/timezone
    const inRange = (dt) => {
      const key = toDateKey(dt);
      if (!key) return false;
      if (start && key < toDateKey(start)) return false;
      if (end && key > toDateKey(end)) return false;
      return true;
    };

    let filteredB = [...bills];
    let filteredP = [...(payments || [])];

    if (selectedParty) {
      filteredB = filteredB.filter((bill) => bill.party?._id === selectedParty);
      filteredP = filteredP.filter((pay) => pay.party?._id === selectedParty);
    }

    if (start && end) {
      filteredB = filteredB.filter((bill) => inRange(bill.date));
      filteredP = filteredP.filter((pay) => inRange(pay.date || pay.createdAt));
    }

    filteredB.sort((a, b) => {
      const dateDiff = new Date(a.date) - new Date(b.date);
      if (dateDiff !== 0) return dateDiff;
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (timeA !== timeB) return timeA - timeB;
      return String(a._id || "").localeCompare(String(b._id || ""));
    });
    filteredP.sort((a, b) => new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date));

    setFilteredBills(filteredB);
    setFilteredPayments(filteredP);

    // Build party due summary (billed, paid, due, quantity per party)
    const summaryMap = new Map();
    const addToSummary = (partyId, partyName) => {
      if (!summaryMap.has(partyId)) {
        summaryMap.set(partyId, { partyId, partyName: partyName || "N/A", billed: 0, paid: 0, quantity: 0 });
      }
      return summaryMap.get(partyId);
    };

    filteredB.forEach((bill) => {
      const entry = addToSummary(bill.party?._id, bill.party?.name);
      entry.billed += parseFloat(bill.total || 0);
      entry.quantity += parseFloat(bill.quantity || 0);
    });

    filteredP.forEach((payment) => {
      const entry = addToSummary(payment.party?._id, payment.party?.name);
      entry.paid += parseFloat(payment.amount || 0);
    });

    // Include parties with carry-forward due even if no transactions in this period
    const DUE_THRESHOLD = 1; // Hide very small balances (< ₹1) to avoid rounding-error noise
    (parties || []).forEach((party) => {
      if (selectedParty && party._id !== selectedParty) return;
      const cumulativeDue = parseFloat(party.duePayment || 0);
      if (cumulativeDue >= DUE_THRESHOLD && !summaryMap.has(party._id)) {
        addToSummary(party._id, party.name);
      }
    });

    // Use cumulative due (party.duePayment) so previous month carry-over is included
    const summary = Array.from(summaryMap.values())
      .map((s) => {
        const party = parties.find((p) => p._id === s.partyId);
        const cumulativeDue = party != null && (party.duePayment != null || party.duePayment === 0)
          ? parseFloat(party.duePayment)
          : Math.max(0, parseFloat((s.billed - s.paid).toFixed(2)));
        return {
          ...s,
          due: parseFloat((cumulativeDue || 0).toFixed(2)),
        };
      })
      .filter((s) => s.billed > 0 || s.paid > 0 || (s.due >= DUE_THRESHOLD))
      .sort((a, b) => b.due - a.due || a.partyName.localeCompare(b.partyName));

    setPartyDueSummary(summary);
  }, [bills, payments, parties, dateRange, startDate, endDate, selectedParty]);

  // Calculate totals
  const calculateTotals = () => {
    const totalQuantity = filteredBills.reduce((sum, bill) => sum + (bill.quantity || 0), 0);
    const totalAmount = filteredBills.reduce((sum, bill) => sum + (bill.total || 0), 0);
    return { totalQuantity, totalAmount };
  };

  // Helper function to load image as base64
  const loadImageAsBase64 = (src) => {
    return new Promise((resolve, reject) => {
      // If src is already a base64 string, return it
      if (typeof src === 'string' && src.startsWith('data:')) {
        resolve(src);
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          resolve(imgData);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        console.error('Error loading image:', error);
        reject(new Error('Failed to load QR code image'));
      };
      
      img.src = src;
    });
  };

  // Generate PDF and return as blob
  const generatePDFBlob = async () => {
    const doc = new jsPDF();
    const { totalQuantity, totalAmount } = calculateTotals();

    // Get page width for positioning
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header - Company name centered
    doc.setFontSize(18);
    doc.text("SHREE HAREKRISHNA GAAJ CENTER", 105, 15, { align: "center" });
    doc.setFontSize(14);
    doc.text("Invoice Details Report", 105, 25, { align: "center" });

    // Add Party Name in big letters below "Invoice Details Report" if party is selected
    if (selectedParty) {
      const party = parties.find((p) => p._id === selectedParty);
      if (party?.name) {
        doc.setFontSize(16);
        doc.setFont(undefined, "bold");
        doc.text(party.name.toUpperCase(), 105, 33, { align: "center" });
        doc.setFont(undefined, "normal");
      }
    }

    // Add QR Code on the right side
    // QR Code size: 35x35mm
    const qrSize = 35;
    const qrX = pageWidth - qrSize - 10; // Right side with 10mm margin
    const qrY = 10;

    // Load and add QR code image
    try {
      const imgData = await loadImageAsBase64(qrImage);
      doc.addImage(imgData, 'JPEG', qrX, qrY, qrSize, qrSize);
    } catch (error) {
      console.warn('Could not load QR code image:', error);
      // Continue without QR code if image fails to load
    }

    // Add Bank Details on the left side
    const bankDetailsX = 14; // Left side
    const bankDetailsY = 10; // Top position
    
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text("Bank Details:", bankDetailsX, bankDetailsY);
    
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.text(`A/c No: 9714069365`, bankDetailsX, bankDetailsY + 5);
    doc.text(`IFSC: KKBK0002572`, bankDetailsX, bankDetailsY + 10);
    doc.text(`Phone: 9429387625`, bankDetailsX, bankDetailsY + 15);

    // Report details (left side, below bank details)
    doc.setFontSize(10);
    let dateInfo = "";
    if (dateRange === "thisMonth") {
      const ranges = getDateRanges();
      dateInfo = `Date: ${formatDate(ranges.thisMonth.start)} to ${formatDate(ranges.thisMonth.end)}`;
    } else if (dateRange === "lastMonth") {
      const ranges = getDateRanges();
      dateInfo = `Date: ${formatDate(ranges.lastMonth.start)} to ${formatDate(ranges.lastMonth.end)}`;
    } else if (dateRange === "custom" && startDate && endDate) {
      dateInfo = `Date: ${formatDate(startDate)} to ${formatDate(endDate)}`;
    }

    doc.text(dateInfo, 14, bankDetailsY + 25);
    doc.text(`Generated: ${formatDate(new Date())}`, 14, bankDetailsY + 30);

    // Table data - Order: NO, DATE, LOT NUMBER, PROCESS, QUANTITY, RATE, AMOUNT
    const tableData = filteredBills.map((bill, index) => [
      index + 1, // NO
      formatDate(bill.date), // DATE
      bill.lotNumber || "N/A", // LOT NUMBER
      bill.process?.name || "N/A", // PROCESS
      bill.quantity || 0, // QUANTITY
      parseFloat(bill.amount || 0).toFixed(2), // RATE
      parseFloat(bill.total || 0).toFixed(2), // AMOUNT
    ]);

    // Add summary row (with total quantity and total amount)
    tableData.push([
      "TOTAL",
      "-",
      "-",
      "-",
      totalQuantity,
      "-",
      totalAmount.toFixed(2),
    ]);

    // Create table - start after header content
    // Adjust startY to account for party name (if shown) and other header content
    const tableStartY = selectedParty ? 55 : 50;
    autoTable(doc, {
      startY: tableStartY,
      head: [["NO", "DATE", "LOTNO", "PROCESS", "QNTY", "RATE", "AMOUNT"]],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [102, 126, 234] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    // Grand total = cumulative outstanding. Split: previous due + this period due = grand total.
    const dueTotal = (() => {
      if (selectedParty) {
        const party = parties.find((p) => p._id === selectedParty);
        return parseFloat(party?.duePayment || 0);
      }
      return parties.reduce(
        (acc, p) => acc + parseFloat(p.duePayment || 0),
        0
      );
    })();
    const totalPaymentsInPeriod = filteredPayments.reduce(
      (sum, p) => sum + parseFloat(p.amount || 0),
      0
    );
    const thisPeriodDue = parseFloat((totalAmount ).toFixed(2));
    const previousDue = parseFloat((dueTotal - thisPeriodDue).toFixed(2));

    let summaryY = doc.lastAutoTable.finalY + 10;
    // If summary would go off-page (e.g. long table), add new page so Billed/Paid lines are visible
    if (summaryY + 28 > pageHeight) {
      doc.addPage();
      summaryY = 20;
    }
    doc.setFontSize(10);
    // doc.text(`Billed (this period): ${formatMoney(totalAmount || 0)}`, 14, summaryY);
    // doc.text(`Paid (this period): ${formatMoney(totalPaymentsInPeriod)}`, 14, summaryY + 6);
    doc.text(`Total Amount (this period): ${formatMoney(thisPeriodDue)}  +  Due (Previous): ${formatMoney(previousDue)}`, 14, summaryY + 14);
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text(`Grand Total (Outstanding): ${formatMoney(dueTotal)}`, 14, summaryY + 22);
    doc.setFont(undefined, "normal");

    // Return PDF as blob
    return doc.output("blob");
  };

  // Generate PDF and download
  const generatePDF = async () => {
    try {
      const blob = await generatePDFBlob();
      const fileName = `Bill_Report_${new Date().toISOString().split("T")[0]}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  // Generate Party Due Summary PDF (separate)
  const generateDueSummaryPDF = async () => {
    if (!partyDueSummary || partyDueSummary.length === 0) {
      alert("No data available for Party Due Summary.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.text("Party Due Summary", 105, 15, { align: "center" });
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text("(Billed/Paid = this period; DUE = cumulative outstanding)", 105, 22, { align: "center" });
    doc.setTextColor(0, 0, 0);

    // Date range info
    doc.setFontSize(10);
    let dateInfo = "";
    if (dateRange === "thisMonth") {
      const ranges = getDateRanges();
      dateInfo = `Date: ${formatDate(ranges.thisMonth.start)} to ${formatDate(ranges.thisMonth.end)}`;
    } else if (dateRange === "lastMonth") {
      const ranges = getDateRanges();
      dateInfo = `Date: ${formatDate(ranges.lastMonth.start)} to ${formatDate(ranges.lastMonth.end)}`;
    } else if (dateRange === "custom" && startDate && endDate) {
      dateInfo = `Date: ${formatDate(startDate)} to ${formatDate(endDate)}`;
    } else {
      dateInfo = "Date: All";
    }
    doc.text(dateInfo, 14, 25);
    doc.text(`Generated: ${formatDate(new Date())}`, 14, 30);

    // Build table (with quantity like total amount)
    const summaryData = partyDueSummary.map((row, idx) => [
      idx + 1,
      row.partyName || "N/A",
      row.quantity ?? 0,
      formatMoney(row.billed || 0),
      formatMoney(row.paid || 0),
      formatMoney(row.due || 0),
    ]);

    const totals = partyDueSummary.reduce(
      (acc, row) => {
        acc.quantity += row.quantity || 0;
        acc.billed += row.billed || 0;
        acc.paid += row.paid || 0;
        acc.due += row.due || 0;
        return acc;
      },
      { quantity: 0, billed: 0, paid: 0, due: 0 }
    );

    summaryData.push([
      "—",
      "TOTAL",
      totals.quantity,
      formatMoney(totals.billed),
      formatMoney(totals.paid),
      formatMoney(totals.due),
    ]);

    autoTable(doc, {
      startY: 40,
      head: [["#", "PARTY", "QTY", "BILLED", "PAID", "DUE"]],
      body: summaryData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    // Download
    const blob = doc.output("blob");
    const fileName = `Party_Due_Summary_${new Date().toISOString().split("T")[0]}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Share PDF via WhatsApp
  const shareViaWhatsApp = async () => {
    try {
      const blob = await generatePDFBlob();
      const fileName = `Bill_Report_${new Date().toISOString().split("T")[0]}.pdf`;
      
      // Create message text
      let message = "📄 Bill Report\n\n";
      if (selectedParty) {
        const party = parties.find((p) => p._id === selectedParty);
        message += `Party: ${party?.name || "N/A"}\n`;
      }
      
      if (dateRange === "thisMonth") {
        const ranges = getDateRanges();
        message += `Date: ${formatDate(ranges.thisMonth.start)} to ${formatDate(ranges.thisMonth.end)}\n`;
      } else if (dateRange === "lastMonth") {
        const ranges = getDateRanges();
        message += `Date: ${formatDate(ranges.lastMonth.start)} to ${formatDate(ranges.lastMonth.end)}\n`;
      } else if (dateRange === "custom" && startDate && endDate) {
        message += `Date: ${formatDate(startDate)} to ${formatDate(endDate)}\n`;
      }
      
      const { totalAmount } = calculateTotals();
      message += `\nTotal Amount: ${formatMoney(totalAmount)}`;

      if (partyDueSummary && partyDueSummary.length > 0) {
        const totals = partyDueSummary.reduce(
          (acc, row) => {
            acc.due += row.due || 0;
            return acc;
          },
          { due: 0 }
        );
        message += `\nTotal Due: ${formatMoney(totals.due)}`;
      }
      message += `\n\nPlease find the attached PDF report.`;
      
      // Check if Web Share API is available (works on mobile and some desktop browsers)
      if (navigator.share) {
        try {
          // Convert blob to File
          const file = new File([blob], fileName, { type: "application/pdf" });
          
          // Check if we can share files
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: "Bill Report",
              text: message,
            });
            return;
          } else if (navigator.canShare && navigator.canShare({ text: message })) {
            // If file sharing not supported, try text sharing
            await navigator.share({
              text: message,
              title: "Bill Report",
            });
            // Download file separately
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return;
          }
        } catch (shareError) {
          // If share fails, continue to WhatsApp Web fallback
          console.log("Web Share API failed, using WhatsApp Web:", shareError);
        }
      }
      
      // Desktop fallback: Open WhatsApp Web with message
      const encodedMessage = encodeURIComponent(message);
      
      // Download the file first so user can attach it
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Open WhatsApp Web with pre-filled message
      // Use wa.me which works better across devices
      const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
      
      // Try to open in new tab
      try {
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      } catch (error) {
        // If popup blocked, redirect current window
        window.location.href = whatsappUrl;
      }
      
      // Clean up URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
    } catch (error) {
      console.error("Error sharing via WhatsApp:", error);
      // Fallback: Just download the PDF
      generatePDF();
    }
  };

  const { totalQuantity, totalAmount } = calculateTotals();
  const paymentsInPeriodTotal = filteredPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const netAmountThisPeriod = parseFloat((totalAmount - paymentsInPeriodTotal).toFixed(2));

  return (
    <>
      <Helmet>
        <title>Bill Report - RTS Books</title>
      </Helmet>
      <style>{`
        .bill-report-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 50%, #c7d2fe 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .bill-report-wrapper {
          max-width: 1600px;
          margin: 0 auto;
        }

        .bill-report-header {
          text-align: center;
          margin-bottom: 30px;
          color: #1e40af;
        }

        .bill-report-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: #1e40af;
          letter-spacing: -0.5px;
        }

        .bill-report-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.08);
          border: 1px solid #e0e7ff;
          margin-bottom: 20px;
        }

        .bill-report-filters {
          display: flex;
          gap: 60px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          align-items: flex-end;
        }

        .bill-report-filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .bill-report-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 6px;
        }

        .bill-report-select,
        .bill-report-input {
          padding: 10px 14px;
          border: 2px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
          color: #1e293b;
          min-width: 150px;
        }

        .bill-report-select:focus,
        .bill-report-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .bill-report-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .bill-report-btn-primary {
          background: #3b82f6;
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
        }

        .bill-report-btn-primary:hover {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .bill-report-btn-success {
          background: #1e40af;
          color: white;
          box-shadow: 0 2px 8px rgba(30, 64, 175, 0.2);
        }

        .bill-report-btn-success:hover {
          background: #1e3a8a;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
        }

        .bill-report-btn-whatsapp {
          background: #25D366;
          color: white;
          box-shadow: 0 2px 8px rgba(37, 211, 102, 0.2);
        }

        .bill-report-btn-whatsapp:hover {
          background: #20BA5A;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
        }

        .bill-report-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .bill-report-btn:disabled:hover {
          transform: none;
        }

        .bill-report-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border-radius: 12px;
          margin-bottom: 20px;
          margin: 0 -30px 20px -30px;
          padding: 0 30px;
        }

        .bill-report-table {
          width: 100%;
          min-width: 700px;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .bill-report-table thead {
          background: #1e40af;
          color: white;
        }

        .bill-report-table th {
          padding: 14px;
          text-align: left;
          font-weight: 600;
          font-size: 0.9rem;
          color: white;
          letter-spacing: 0.3px;
        }

        .bill-report-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #e2e8f0;
          color: #1e293b;
        }

        .bill-report-table tbody tr:hover {
          background: #f1f5f9;
        }

        .bill-report-table tbody tr:nth-child(even) {
          background: #fafbfc;
        }

        .bill-report-table tbody tr:nth-child(even):hover {
          background: #f1f5f9;
        }

        .bill-report-table tbody tr:last-child td {
          border-bottom: none;
        }

        .bill-report-summary {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border: 2px solid #3b82f6;
          border-radius: 12px;
          padding: 20px;
          margin-top: 20px;
        }

        .bill-report-summary-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 15px;
          letter-spacing: 0.3px;
        }

        .bill-report-summary-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #bfdbfe;
        }

        .bill-report-summary-row:last-child {
          border-bottom: none;
          font-weight: 700;
          font-size: 1.1rem;
          color: #1e3a8a;
          padding-top: 12px;
          margin-top: 8px;
          border-top: 2px solid #3b82f6;
        }

        .bill-report-summary-label {
          color: #1e293b;
          font-weight: 600;
        }

        .bill-report-summary-value {
          color: #1e40af;
          font-weight: 700;
        }

        /* Large Tablets */
        @media (max-width: 1024px) {
          .bill-report-container {
            padding: 15px;
          }

          .bill-report-wrapper {
            max-width: 100%;
          }

          .bill-report-title {
            font-size: 2.2rem;
          }

          .bill-report-card {
            padding: 25px;
          }

          .bill-report-filters {
            gap: 12px;
          }

          .bill-report-table-wrapper {
            margin: 0 -25px 20px -25px;
            padding: 0 25px;
          }
        }

        /* Tablets */
        @media (max-width: 768px) {
          .bill-report-container {
            padding: 15px 10px;
          }

          .bill-report-header {
            margin-bottom: 20px;
          }

          .bill-report-title {
            font-size: 2rem;
            margin-bottom: 8px;
          }

          .bill-report-card {
            padding: 20px;
            border-radius: 15px;
          }

          .bill-report-filters {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }

          .bill-report-filter-group {
            width: 100%;
          }

          .bill-report-select,
          .bill-report-input {
            width: 100%;
            min-width: 100%;
            padding: 12px 14px;
          }

          .bill-report-btn {
            width: 100%;
            justify-content: center;
            padding: 12px 20px;
          }

          .bill-report-table-wrapper {
            margin: 0 -20px 20px -20px;
            padding: 0 20px;
          }

          .bill-report-table {
            min-width: 650px;
            font-size: 0.85rem;
          }

          .bill-report-table th,
          .bill-report-table td {
            padding: 10px 8px;
            font-size: 0.85rem;
          }

          .bill-report-summary {
            padding: 18px;
            margin-top: 15px;
          }

          .bill-report-summary-title {
            font-size: 1.1rem;
            margin-bottom: 12px;
          }

          .bill-report-summary-row {
            padding: 8px 0;
            flex-wrap: wrap;
            gap: 8px;
          }
        }

        /* Mobile Phones */
        @media (max-width: 480px) {
          .bill-report-container {
            padding: 10px 8px;
          }

          .bill-report-header {
            margin-bottom: 15px;
          }

          .bill-report-title {
            font-size: 1.5rem;
            line-height: 1.2;
          }

          .bill-report-card {
            padding: 15px;
            border-radius: 12px;
          }

          .bill-report-filters {
            gap: 12px;
            margin-bottom: 15px;
          }

          .bill-report-label {
            font-size: 0.85rem;
            margin-bottom: 5px;
          }

          .bill-report-select,
          .bill-report-input {
            padding: 10px 0px;
            font-size: 0.9rem;
          }

          .bill-report-btn {
            padding: 12px 16px;
            font-size: 0.9rem;
          }

          .bill-report-table-wrapper {
            margin: 0 -15px 15px -15px;
            padding: 0 15px;
          }

          .bill-report-table {
            min-width: 600px;
            font-size: 0.8rem;
            border-radius: 8px;
          }

          .bill-report-table th,
          .bill-report-table td {
            padding: 8px 6px;
            font-size: 0.8rem;
          }

          .bill-report-table th {
            font-size: 0.75rem;
            padding: 10px 6px;
          }

          .bill-report-summary {
            padding: 15px;
            border-radius: 10px;
            margin-top: 15px;
          }

          .bill-report-summary-title {
            font-size: 1rem;
            margin-bottom: 10px;
          }

          .bill-report-summary-row {
            padding: 10px 0;
            font-size: 0.9rem;
            flex-direction: column;
            gap: 4px;
            align-items: flex-start;
          }

          .bill-report-summary-row:last-child {
            font-size: 1rem;
            flex-direction: row;
            justify-content: space-between;
            padding-top: 12px;
            margin-top: 8px;
            border-top: 2px solid #86efac;
          }

          .bill-report-summary-label {
            font-size: 0.9rem;
          }

          .bill-report-summary-value {
            font-size: 0.95rem;
          }
        }

        /* Extra Small Mobile */
        @media (max-width: 360px) {
          .bill-report-container {
            padding: 8px 6px;
          }

          .bill-report-title {
            font-size: 1.3rem;
          }

          .bill-report-card {
            padding: 12px;
          }

          .bill-report-table-wrapper {
            margin: 0 -12px 12px -12px;
            padding: 0 12px;
          }

          .bill-report-table {
            min-width: 550px;
            font-size: 0.75rem;
          }

          .bill-report-table th,
          .bill-report-table td {
            padding: 6px 4px;
            font-size: 0.75rem;
          }

          .bill-report-summary {
            padding: 12px;
          }

          .bill-report-summary-title {
            font-size: 0.95rem;
          }

          .bill-report-summary-row {
            font-size: 0.85rem;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          .bill-report-container {
            padding: 12px 10px;
          }

          .bill-report-header {
            margin-bottom: 15px;
          }

          .bill-report-title {
            font-size: 1.8rem;
          }

          .bill-report-filters {
            flex-direction: row;
            flex-wrap: wrap;
          }

          .bill-report-filter-group {
            flex: 1;
            min-width: 150px;
          }

          .bill-report-btn {
            width: auto;
            flex: 0 0 auto;
          }
        }
      `}</style>

      <div className="bill-report-container">
        <div className="bill-report-wrapper">
          <div className="bill-report-header">
            <h1 className="bill-report-title">Bill Report / Invoice</h1>
          </div>

          <div className="bill-report-card">
            <div className="bill-report-filters">
              <div className="bill-report-filter-group">
                <label className="bill-report-label">Date Range</label>
                <select
                  className="bill-report-select"
                  value={dateRange}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDateRange(val);
                    if (val === "custom") {
                      setStartDate("");
                      setEndDate("");
                    } else {
                      const r = getDateRanges();
                      const range = val === "lastMonth" ? r.lastMonth : r.thisMonth;
                      setStartDate(toDateKey(range.start));
                      setEndDate(toDateKey(range.end));
                    }
                  }}
                >
                  <option value="thisMonth">This Month</option>
                  <option value="lastMonth">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {dateRange === "custom" && (
                <>
                  <div className="bill-report-filter-group">
                    <label className="bill-report-label">Start Date</label>
                    <input
                      type="date"
                      className="bill-report-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="bill-report-filter-group">
                    <label className="bill-report-label">End Date</label>
                    <input
                      type="date"
                      className="bill-report-input"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="bill-report-filter-group">
                <label className="bill-report-label">Party (Optional)</label>
                <SearchableSelect
                  options={[{ _id: "", name: "All Parties" }, ...parties]}
                  value={selectedParty}
                  onChange={(e) => setSelectedParty(e.target.value)}
                  placeholder="Search party by name or number..."
                  getOptionLabel={(party) => party.name || "All Parties"}
                  getOptionValue={(party) => party._id || ""}
                  searchFields={["name", "number", "_id"]}
                  className="bill-report-select"
                />
              </div>

              <button
                className="bill-report-btn bill-report-btn-success"
                onClick={generatePDF}
                disabled={filteredBills.length === 0}
              >
                📥 Download PDF
              </button>

              <button
                className="bill-report-btn bill-report-btn-primary"
                onClick={generateDueSummaryPDF}
                disabled={partyDueSummary.length === 0}
              >
                📑 Download Due Summary
              </button>

              <button
                className="bill-report-btn bill-report-btn-whatsapp"
                onClick={shareViaWhatsApp}
                disabled={filteredBills.length === 0}
              >
                📱 Share on WhatsApp
              </button>
            </div>

            {loading && <div style={{ textAlign: "center", padding: "40px", color: "#1e40af", fontSize: "1rem" }}>Loading...</div>}

            {!loading && filteredBills.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#64748b", fontSize: "1rem" }}>
                No bills found for the selected criteria
              </div>
            )}

            {!loading && filteredBills.length > 0 && (
              <>
                <div className="bill-report-table-wrapper">
                  <table className="bill-report-table">
                    <thead>
                      <tr> 
                        <th>NO</th>
                        <th>QNTY</th>
                        <th>RATE</th>
                        <th>AMOUNT</th>
                        <th>PROCESS</th>
                        <th>LOTNO</th>
                        <th>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBills.map((bill, index) => (
                        <tr key={bill._id}>
                          <td>{index + 1}</td>
                          <td>{bill.quantity}</td>
                          <td>₹{parseFloat(bill.amount).toFixed(2)}</td>
                          <td>₹{parseFloat(bill.total).toFixed(2)}</td>
                          <td>{bill.process?.name || "N/A"}</td>
                          <td>{bill.lotNumber}</td>
                          <td>{new Date(bill.date).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bill-report-summary">
                  <div className="bill-report-summary-title">Summary</div>
                  <div className="bill-report-summary-row">
                    <span className="bill-report-summary-label">Total Quantity:</span>
                    <span className="bill-report-summary-value">{totalQuantity}</span>
                  </div>
                  <div className="bill-report-summary-row">
                    <span className="bill-report-summary-label">Billed (this period):</span>
                    <span className="bill-report-summary-value">₹{totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="bill-report-summary-row">
                    <span className="bill-report-summary-label">Paid (this period):</span>
                    <span className="bill-report-summary-value">₹{paymentsInPeriodTotal.toFixed(2)}</span>
                  </div>
                  <div className="bill-report-summary-row">
                    <span className="bill-report-summary-label">Total Amount (this period):</span>
                    <span className="bill-report-summary-value">₹{netAmountThisPeriod.toFixed(2)}</span>
                  </div>
                  {selectedParty && (
                    <div className="bill-report-summary-row">
                      <span className="bill-report-summary-label">Outstanding Amount:</span>
                      <span className="bill-report-summary-value">
                        ₹{(() => {
                          const party = parties.find((p) => p._id === selectedParty);
                          if (party) {
                            const outstanding = (party.duePayment || 0) - (party.advanceAmount || 0);
                            return outstanding.toFixed(2);
                          }
                          return "0.00";
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BillReport;

